<template>
  <div class="media-hotspot relative flex flex-col items-center m-auto text-white rounded-lg shadow-xl overflow-hidden">
    <h3 class="text-2xl text-center mt-4 mb-2">{{ hotspot.title }}</h3>
    <p
      v-if="hotspot.content"
      class="image-description absolute bottom-0 left-0 right-0 p-4 pb-6 m-0 bg-black bg-opacity-60 transition-all whitespace-pre-line z-30"
      :class="{ 'description-open': isDescriptionOpenLocally }"
      @click="toggleImageDescription"
    >
      <ChevronDownIcon class="icon w-6 m-auto transition-transform block mb-4 cursor-pointer pointer-events-auto" />
      {{ hotspot.content }}
    </p>
    <div v-if="type === 'I'" ref="container" class="relative">
      <div class="annotations-canvas" />
      <img
        ref="image"
        class="relative max-w-full z-10"
        :src="hotspot.media[0].url || undefined"
        :style="{ ...(contentStyle as CSSStyleValue), maxWidth: `${width}px`, maxHeight: `${height}px` }"
      />
    </div>
    <iframe
      v-else
      class="max-w-full"
      width="560"
      height="315"
      :src="hotspot.media[0].url || undefined"
      frameborder="0"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
    />
  </div>
</template>

<script lang="ts" setup>
import { ChevronDownIcon } from '@heroicons/vue/20/solid'
import type { Hotspot, HotspotType } from '@/entities/site'
import { useImageLoadEvent } from '@/composables/useImageLoadEvent'

const props = defineProps({
  hotspot: {
    type: Object as PropType<Hotspot>,
    required: true,
  },
  type: {
    type: String as PropType<HotspotType>,
    required: true,
  },
  width: {
    type: Number,
    required: true,
  },
  height: {
    type: Number,
    required: true,
  },
  contentStyle: {
    type: Object as PropType<Partial<CSSStyleDeclaration>>,
    default: () => ({}),
  },
  isDescriptionOpen: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['update:image-dimensions', 'toggle-image-description'])

const { isDescriptionOpen } = toRefs(props)
const image = ref<HTMLImageElement | undefined>()
const isDescriptionOpenLocally = ref(false)

useImageLoadEvent(image, img => {
  emit('update:image-dimensions', { width: img.naturalWidth, height: img.naturalHeight })
})

watch(
  isDescriptionOpen,
  isDescriptionOpen => {
    isDescriptionOpenLocally.value = isDescriptionOpen
  },
  { immediate: true }
)

function toggleImageDescription() {
  isDescriptionOpenLocally.value = !isDescriptionOpenLocally.value
  emit('toggle-image-description')
}
</script>

<style lang="stylus" scoped>
.media-hotspot
  max-height 80vh
  background radial-gradient(#4a4a4a, #212121)
  img
    max-height 80vh
.image-description
  height 25%
  .icon
    transform rotate(180deg)
  &.description-open
    overflow-y auto
    height calc(100% - 3.5rem)
    .icon
      transform rotate(0)
</style>
